// Copy-bearing platform selector model for the Creative Studio AI Copy
// generator.
//
// This is now a thin derivation over the richer platform copy spec layer
// (creativeCopyPlatformSpecs) so there is a SINGLE source of truth. A platform
// is offered in the AI Copy platform selector only when its copy model is
// "platform_ad_copy" (it consumes separate text fields). Display / CTV / DOOH
// are "creative_guidance" platforms — they embed copy in the creative, so they
// are not offered as deployable copy targets here.
//
// IMPORTANT: this restriction is scoped to the AI Copy platform selector only.
// It does NOT affect creative eligibility, Creative Assets, trafficking,
// reporting, platform chips, analysis, or governance.

import {
  listCopyPlatformSpecs,
  normalizeCopySpecPlatformKey,
  isPlatformAdCopyPlatform,
  getCopyPlatformSpec,
} from "./creativeCopyPlatformSpecs";

export const COPY_BEARING_PLATFORMS = Object.freeze(
  listCopyPlatformSpecs()
    .filter((spec) => spec.copyModel === "platform_ad_copy")
    .map((spec) => Object.freeze({ value: spec.key, label: spec.label }))
);

export function normalizeCopyPlatformKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

// Returns the canonical key for a value if it is a copy-bearing (platform ad
// copy) platform, else "".
export function normalizeCopyBearingPlatform(value) {
  const key = normalizeCopySpecPlatformKey(value);
  if (!key) return "";
  return isPlatformAdCopyPlatform(key) ? key : "";
}

export function isCopyBearingPlatform(value) {
  return Boolean(normalizeCopyBearingPlatform(value));
}

// Picks the first copy-bearing platform from candidate values (e.g. the
// creative's studioPlatform, then its channel). Returns "" when none are
// copy-bearing — that drives the "Display banners use embedded creative text"
// unavailable state in the AI Copy panel.
export function resolveDefaultCopyPlatform(...candidates) {
  for (const candidate of candidates) {
    const match = normalizeCopyBearingPlatform(candidate);
    if (match) return match;
  }
  return "";
}

// True when a value maps to a known copy spec platform but one that uses
// embedded creative copy (Display/CTV/DOOH) rather than platform ad-copy fields.
export function isCreativeGuidancePlatform(value) {
  const spec = getCopyPlatformSpec(value);
  return Boolean(spec && spec.copyModel === "creative_guidance");
}

export const AI_COPY_NO_COPY_PLATFORM_NOTICE =
  "AI copy is available for copy-bearing platforms. Display banners use embedded creative text — choose a copy-bearing platform (Meta, TikTok, YouTube, LinkedIn, …) to generate copy.";

// Canonical left-to-right order for the AI Copy platform selector chips. Any
// copy-bearing platform not listed here sorts after these, alphabetically by
// label, so the selector stays stable regardless of campaign data ordering.
export const COPY_PLATFORM_DISPLAY_ORDER = Object.freeze([
  "facebook",
  "instagram",
  "tiktok",
  "youtube",
  "meta",
  "linkedin",
  "google_search",
]);

// Copy-bearing platforms always offered in the AI Copy selector (alongside the
// campaign-derived set) whenever any copy platform is available.
export const COPY_PLATFORM_ALWAYS_OFFERED = Object.freeze(["tiktok"]);

function buildOfferedCopyPlatformOption(key) {
  const spec = getCopyPlatformSpec(key);
  return { value: spec?.key || key, label: spec?.label || key, source: "default" };
}

// Presents copy-platform options in the canonical selector order and guarantees
// the always-offered platforms (e.g. TikTok) are available whenever copy chips
// are shown. Empty input is returned untouched so guidance/empty states are
// unaffected. Only ordering and chip membership change — selection behaviour and
// styling are preserved by callers.
export function finalizeCopyPlatformOptions(options) {
  const list = Array.isArray(options) ? options.slice() : [];
  if (!list.length) return list;

  const present = new Set(list.map((option) => option.value));
  COPY_PLATFORM_ALWAYS_OFFERED.forEach((key) => {
    if (isCopyBearingPlatform(key) && !present.has(key)) {
      list.push(buildOfferedCopyPlatformOption(key));
      present.add(key);
    }
  });

  const rank = (value) => {
    const index = COPY_PLATFORM_DISPLAY_ORDER.indexOf(value);
    return index === -1 ? COPY_PLATFORM_DISPLAY_ORDER.length : index;
  };
  return list.sort((a, b) => {
    const diff = rank(a.value) - rank(b.value);
    if (diff !== 0) return diff;
    return String(a.label).localeCompare(String(b.label));
  });
}
